import { useRef } from 'react';
import type { FormField, FormSchema } from '../types.js';
import FieldEditor from './FieldEditor.js';

interface Props {
  schema: FormSchema;
  onChange: (schema: FormSchema) => void;
}

const BLANK_FIELD: FormField = {
  name: '',
  type: 'text',
  label: '',
  required: false,
};

export default function SchemaEditor({ schema, onChange }: Props) {
  const dragIdx = useRef<number | null>(null);

  function updateField(idx: number, updated: FormField) {
    const fields = [...schema.fields];
    fields[idx] = updated;
    onChange({ ...schema, fields });
  }

  function removeField(idx: number) {
    const fields = schema.fields.filter((_, i) => i !== idx);
    onChange({ ...schema, fields });
  }

  function addField() {
    onChange({ ...schema, fields: [...schema.fields, { ...BLANK_FIELD }] });
  }

  function updateMeta(patch: Partial<FormSchema>) {
    onChange({ ...schema, ...patch });
  }

  // HTML5 drag-and-drop reorder
  function onDragStart(idx: number) {
    dragIdx.current = idx;
  }

  function onDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const fields = [...schema.fields];
    const [moved] = fields.splice(dragIdx.current, 1);
    if (moved) {
      fields.splice(idx, 0, moved);
      dragIdx.current = idx;
      onChange({ ...schema, fields });
    }
  }

  function onDragEnd() {
    dragIdx.current = null;
  }

  return (
    <div>
      <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Fields</h3>

      {schema.fields.map((field, idx) => (
        <div
          key={idx}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragOver={(e) => onDragOver(e, idx)}
          onDragEnd={onDragEnd}
          style={{ cursor: 'grab' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.25rem', color: '#94a3b8', fontSize: '.8rem' }}>
            ⠿ drag to reorder
          </div>
          <FieldEditor
            field={field}
            onChange={(updated) => updateField(idx, updated)}
            onRemove={() => removeField(idx)}
          />
        </div>
      ))}

      <button type="button" className="btn btn-secondary" onClick={addField} style={{ marginBottom: '1.5rem' }}>
        + Add field
      </button>

      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '1rem 0 .75rem' }}>Form settings</h3>

      <div className="form-group">
        <label>Submit button text</label>
        <input
          type="text"
          value={schema.submit_button_text ?? ''}
          onChange={(e) => updateMeta({ submit_button_text: e.target.value || undefined })}
          placeholder="Submit"
        />
      </div>
      <div className="form-group">
        <label>Success message</label>
        <input
          type="text"
          value={schema.success_message ?? ''}
          onChange={(e) => updateMeta({ success_message: e.target.value || undefined })}
          placeholder="Thanks for your submission. We'll be in touch."
        />
        <div className="form-hint">Shown on the same page after a successful submit (ignored if a redirect URL is set below).</div>
      </div>
      <div className="form-group">
        <label>Thank you page URL (optional)</label>
        <input
          type="url"
          value={schema.success_redirect_url ?? ''}
          onChange={(e) => updateMeta({ success_redirect_url: e.target.value || undefined })}
          placeholder="https://your-site.com/thank-you"
        />
        <div className="form-hint">
          When set, a successful submission redirects to this URL with every field value appended as a query string (e.g. <code>?name=John&amp;phone=0412</code>). Leave blank to show the success message above.
        </div>
      </div>
      <div className="form-group">
        <label>Email subject template</label>
        <input
          type="text"
          value={schema.email_subject_template ?? ''}
          onChange={(e) => updateMeta({ email_subject_template: e.target.value || undefined })}
          placeholder="New submission from {{name}}"
        />
        <div className="form-hint">Use {'{{field_name}}'} to insert field values.</div>
      </div>
      <div className="form-group">
        <label>Email body template (HTML, optional)</label>
        <textarea
          value={schema.email_body_template ?? ''}
          onChange={(e) => updateMeta({ email_body_template: e.target.value || undefined })}
          style={{ minHeight: '120px', fontFamily: 'monospace', fontSize: '.85rem' }}
          placeholder="Leave blank for auto-generated table of all fields."
        />
        <div className="form-hint">Use {'{{field_name}}'} to insert field values. Values are HTML-escaped automatically.</div>
      </div>
    </div>
  );
}
